import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { History, X, Search, Plus, Trash2, Edit3, Check } from 'lucide-react';
import { MealTimeSelector } from '../components/MealTimeSelector';
import { toast } from 'sonner';

export function SavedBarcodesModal({
  isOpen,
  onClose,
  savedBarcodes,
  onAddFood,
  onDeleteBarcode,
  onUpdateBarcode,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);
  const [editingBarcode, setEditingBarcode] = useState(null);
  const [editName, setEditName] = useState('');
  const [portions, setPortions] = useState({});

  // Convert savedBarcodes object to array
  const barcodesList = useMemo(() => {
    return Object.entries(savedBarcodes).map(([barcode, product]) => ({
      barcode,
      ...product,
    }));
  }, [savedBarcodes]);

  // Filter based on search
  const filteredBarcodes = useMemo(() => {
    if (!searchQuery.trim()) return barcodesList;
    const search = searchQuery.toLowerCase();
    return barcodesList.filter(p => 
      p.name?.toLowerCase().includes(search) ||
      p.brand?.toLowerCase().includes(search) ||
      p.barcode?.includes(search)
    );
  }, [barcodesList, searchQuery]);

  const handleClose = () => {
    setSearchQuery('');
    setEditingBarcode(null);
    setEditName('');
    onClose();
  };

  const handleSaveEdit = (barcode) => {
    if (editName.trim() && onUpdateBarcode) {
      onUpdateBarcode(barcode, { name: editName.trim() });
      toast.success('Product name updated');
    }
    setEditingBarcode(null);
    setEditName('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <History className="h-5 w-5 text-violet-400" />
            My Scanned Products
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        {/* Search */}
        <div className="px-6 pb-3 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, brand or barcode..."
              className="pl-10 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          {/* Meal Time Selection */}
          <div className="space-y-1">
            <label className="text-sm text-slate-400">Add to:</label>
            <MealTimeSelector 
              selectedMealTime={selectedMealTime} 
              onMealTimeChange={setSelectedMealTime}
              compact
              mealTimes={mealTimes}
            />
          </div>
        </div>
        
        <CardContent className="overflow-y-auto flex-1">
          {filteredBarcodes.length > 0 ? (
            <div className="space-y-2">
              {filteredBarcodes.map((product) => {
                const portionG = parseFloat(portions[product.barcode]);
                const hasPortion = portionG > 0;
                const scale = hasPortion ? portionG / 100 : 1;
                const scaled = {
                  calories: Math.round((product.calories || 0) * scale),
                  protein: Math.round(((product.protein || 0) * scale) * 10) / 10,
                  carbs: Math.round(((product.carbs || 0) * scale) * 10) / 10,
                  fat: Math.round(((product.fat || 0) * scale) * 10) / 10,
                };
                return (
                <div key={product.barcode} className="p-3 bg-slate-700/50 rounded-lg">
                  <div className="flex items-start gap-3">
                    {product.image && (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-12 h-12 object-cover rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      {editingBarcode === product.barcode ? (
                        <div className="flex items-center gap-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="bg-slate-600 border-slate-500 text-white text-sm h-7"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveEdit(product.barcode);
                              if (e.key === 'Escape') { setEditingBarcode(null); setEditName(''); }
                            }}
                          />
                          <button onClick={() => handleSaveEdit(product.barcode)}
                            className="text-green-400 hover:text-green-300 p-1" data-testid="save-edit-name-btn">
                            <Check className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <p className="text-white font-medium text-sm truncate">{product.name}</p>
                          <button onClick={() => { setEditingBarcode(product.barcode); setEditName(product.name || ''); }}
                            className="text-amber-400 hover:text-amber-300 p-0.5 flex-shrink-0"
                            data-testid={`edit-barcode-name-${product.barcode}`}>
                            <Edit3 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      )}
                      {product.brand && (
                        <p className="text-slate-500 text-xs truncate">{product.brand}</p>
                      )}
                      <p className="text-slate-600 text-xs font-mono">{product.barcode}</p>
                      <div className="flex gap-2 mt-1 text-xs">
                        <span className="text-orange-400">{scaled.calories} cal</span>
                        <span className="text-red-400">P:{scaled.protein}g</span>
                        <span className="text-yellow-400">C:{scaled.carbs}g</span>
                        <span className="text-blue-400">F:{scaled.fat}g</span>
                      </div>
                    </div>
                  </div>
                  {/* Portion size + Add button */}
                  <div className="flex gap-2 mt-2 items-center">
                    <div className="flex items-center gap-1.5 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1">
                      <input
                        type="number"
                        inputMode="numeric"
                        value={portions[product.barcode] ?? ''}
                        onChange={(e) => setPortions(prev => ({ ...prev, [product.barcode]: e.target.value }))}
                        className="w-14 bg-transparent text-white text-sm font-medium focus:outline-none text-center"
                        placeholder="g"
                        data-testid={`portion-input-${product.barcode}`}
                      />
                      <span className="text-slate-400 text-xs">g</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => onAddFood({
                        ...product,
                        calories: scaled.calories,
                        protein: scaled.protein,
                        carbs: scaled.carbs,
                        fat: scaled.fat,
                        grams: hasPortion ? portionG : null,
                        mealTime: selectedMealTime,
                      })}
                      className="flex-1 bg-green-600 hover:bg-green-500"
                      data-testid={`add-barcode-${product.barcode}`}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onDeleteBarcode(product.barcode)}
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      data-testid={`delete-barcode-${product.barcode}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="h-12 w-12 text-slate-600 mx-auto mb-2" />
              <p className="text-slate-400">
                {searchQuery ? 'No products found' : 'No scanned products yet'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Scan barcodes to save products for quick access
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SavedBarcodesModal;
